import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// ── Warm co-watch data cache ─────────────────────────────────────────────────
// "Watching with friends" (TonightTogether) needs two round-trips — followed
// friends' watching rows (the in-sync source) + declared co-watch pacts. Fetched
// fresh on Tonight open, they landed ~0.5s after paint and shoved the page. This
// module fetches them ONCE per (user, follow-set) and caches the result, so a
// prefetch fired while the user is still on Home leaves the data WARM by the time
// Tonight opens — the section renders complete on first paint, on top, no shift.
//
// Structurally safe: the hook ALWAYS resolves the data (fetch-if-cold), like
// useFollows — so the co-watch feature works whether or not a prefetch ran; the
// prefetch only changes WHEN the data is ready, never WHETHER it's there. Shapes
// match TonightTogether's old local state exactly (rawRows/profMap/pactRows/
// pactProf/pactProg) so the render logic is untouched.
//
// Keyed by `userId:<followCount>` and only fetched once follows have RESOLVED, so
// an early call (before useFollows returns) can't cache an empty-friends result,
// and a follow/unfollow (count change) busts to a fresh fetch.

const EMPTY = { rawRows: [], profMap: {}, pactRows: [], pactProf: {}, pactProg: {}, ready: false }
const cache   = new Map()   // "userId:count" -> resolved data (ready:true)
const pending = new Map()   // "userId:count" -> Promise
const subs    = new Set()

const notify = () => subs.forEach(fn => { try { fn() } catch {} })
const keyOf  = (userId, followingIds) => `${userId}:${(followingIds || []).length}`

async function run(cacheKey, userId, fIds) {
  // Each leg is defensive: a failed query degrades to empty for THAT leg rather
  // than throwing away the whole section (matches the old per-effect resilience —
  // a pacts hiccup never hid the in-sync list, and vice-versa). Always resolves
  // ready:true so the UI never hangs on a skeleton.
  let rawRows = [], profMap = {}, pactRows = [], pactProf = {}, pactProg = {}

  try {
    const [{ data: rows }, { data: profiles }] = await Promise.all([
      fIds.length
        ? supabase.from('public_watchlist').select('user_id, show_id, show_data, progress')
            .in('user_id', fIds).eq('status', 'watching').limit(300)
        : Promise.resolve({ data: [] }),
      fIds.length
        ? supabase.from('profiles').select('id, display_name, username').in('id', fIds)
        : Promise.resolve({ data: [] }),
    ])
    rawRows = rows || []
    ;(profiles || []).forEach(pr => { profMap[pr.id] = pr })
  } catch { /* in-sync source unavailable, leave empty */ }

  try {
    // Co-watch pacts — normalize each to "the OTHER person" (surfaces on both sides).
    const { data: pactData } = await supabase.from('cowatch')
      .select('id, show_id, show_data, user_id, partner_id')
      .or(`user_id.eq.${userId},partner_id.eq.${userId}`)
      .order('created_at', { ascending: false })
    pactRows = (pactData || []).map(r => ({ ...r, otherId: r.user_id === userId ? r.partner_id : r.user_id }))

    const ids     = [...new Set(pactRows.map(r => r.otherId))]
    const showIds = [...new Set(pactRows.map(r => r.show_id))]
    if (ids.length) {
      const [{ data: profs }, { data: wls }] = await Promise.all([
        supabase.from('profiles').select('id, display_name, username').in('id', ids),
        supabase.from('public_watchlist').select('user_id, show_id, progress, status').in('user_id', ids).in('show_id', showIds),
      ])
      ;(profs || []).forEach(pr => { pactProf[pr.id] = pr })
      ;(wls || []).forEach(w => { pactProg[`${w.user_id}:${w.show_id}`] = { progress: w.progress, status: w.status } })
    }
  } catch { /* pacts unavailable, leave empty */ }

  const data = { rawRows, profMap, pactRows, pactProf, pactProg, ready: true }
  cache.set(cacheKey, data)
  pending.delete(cacheKey)
  notify()
  return data
}

export function fetchCoWatch(userId, followingIds) {
  if (!userId) return Promise.resolve(EMPTY)
  const cacheKey = keyOf(userId, followingIds)
  if (cache.has(cacheKey))   return Promise.resolve(cache.get(cacheKey))
  if (pending.has(cacheKey)) return pending.get(cacheKey)
  const p = run(cacheKey, userId, followingIds || []).catch(() => { pending.delete(cacheKey); return EMPTY })
  pending.set(cacheKey, p)
  return p
}

// Fire-and-forget warm-up — call once follows have RESOLVED (see App).
export function prefetchCoWatch(userId, followingIds, followsReady) {
  if (userId && followsReady) fetchCoWatch(userId, followingIds)
}

// Consumer hook. Returns resolved data (ready:false + empty shapes until then).
// Only fetches once `followsReady`, so it can't cache a pre-resolution empty set.
export function useCoWatch(userId, followingIds, followsReady) {
  const cacheKey = keyOf(userId, followingIds)
  const [data, setData] = useState(() => cache.get(cacheKey) || EMPTY)
  useEffect(() => {
    let alive = true
    const sync = () => { if (alive) setData(cache.get(cacheKey) || EMPTY) }
    subs.add(sync)
    sync()
    if (userId && followsReady) fetchCoWatch(userId, followingIds).then(sync)
    return () => { alive = false; subs.delete(sync) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, followsReady])
  return data
}
